import { useMemo, useState } from "react";
import { AlertTriangle, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { formatPriceCents } from "@/components/services/service-meta";
import { LinhaDeConsumo } from "./LinhaDeConsumo";
import { EquipamentoDoServico } from "./EquipamentoDoServico";
import { agruparMateriais, rotuloDoMaterial } from "@/lib/agrupar-materiais";

const NOVA_CATEGORIA = "cat:";

/**
 * O que este serviço consumiu de material.
 *
 * Nada é aplicado sozinho: o par serviço × material pode ter várias receitas com custos bem
 * diferentes, e escolher por conta própria seria inventar o custo do mês. O protético diz qual
 * usou — a qualquer momento da produção — e a partir daí edita o que gastou de fato.
 */
export function MateriaisDoServico({ serviceId }: { serviceId: string }) {
  const utils = trpc.useUtils();
  const podeEditar = usePermissao("production.manage");
  const [confirmandoTroca, setConfirmandoTroca] = useState<string | null>(null);

  const query = trpc.consumo.porServico.useQuery({ serviceId }, { enabled: !!serviceId });
  const materiaisQuery = trpc.prosthesis.catalog.myMaterialLines.useQuery(undefined, { enabled: podeEditar });

  const invalidar = () => {
    utils.consumo.porServico.invalidate({ serviceId });
    utils.consumo.resumoDaProducao.invalidate();
  };
  const aoFalhar = (e: { message: string }) => toast.error(e.message || "Não foi possível salvar.");

  const aplicar = trpc.consumo.aplicarFicha.useMutation({
    onSuccess: () => {
      toast.success("Ficha aplicada.");
      setConfirmandoTroca(null);
      invalidar();
    },
    onError: (e) => {
      if (e.data?.code === "CONFLICT") return; // a tela já pergunta antes; este é o caminho do "substituir"
      aoFalhar(e);
    },
  });
  const resolver = trpc.consumo.resolverLinha.useMutation({ onSuccess: invalidar, onError: aoFalhar });
  const atualizar = trpc.consumo.atualizarLinha.useMutation({ onSuccess: invalidar, onError: aoFalhar });
  const adicionar = trpc.consumo.adicionarLinha.useMutation({ onSuccess: invalidar, onError: aoFalhar });
  const remover = trpc.consumo.removerLinha.useMutation({ onSuccess: invalidar, onError: aoFalhar });

  const dados = query.data;
  const linhas = dados?.lines ?? [];
  const podeVerCusto = dados?.podeVerCusto ?? false;

  // Material ativo, agrupado como no catálogo — pela categoria do laboratório (Brocas, Resinas).
  const grupos = useMemo(() => agruparMateriais((materiaisQuery.data ?? []) as any[]), [materiaisQuery.data]);

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
      </div>
    );
  }
  if (!dados) return null;

  const { servico, fichas, custo } = dados;
  const semNada = linhas.length === 0;

  function adicionarMaterial(valor: string) {
    if (!valor) return;
    if (valor.startsWith(NOVA_CATEGORIA)) {
      adicionar.mutate({ serviceId, materialCategoryId: valor.slice(NOVA_CATEGORIA.length), quantityNet: 1, correctionFactor: 1 });
      return;
    }
    const m = (materiaisQuery.data ?? []).find((x: any) => x.id === valor);
    adicionar.mutate({
      serviceId,
      materialLineId: valor,
      quantityNet: 1,
      correctionFactor: m?.defaultCorrectionFactor != null ? Number(m.defaultCorrectionFactor) : 1,
    });
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-zinc-50 px-3 py-2 dark:bg-muted/40">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          Produção de
        </p>
        <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">
          {servico.servicoLabel}
          {servico.materialLabel && (
            <span className="font-normal text-zinc-500 dark:text-muted-foreground"> · {servico.materialLabel}</span>
          )}
        </p>
        {servico.technicalSheetName && (
          <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-muted-foreground">
            Ficha aplicada: <strong className="font-semibold">{servico.technicalSheetName}</strong>
          </p>
        )}
      </div>

      {/* Trocaram o material do serviço depois da ficha aplicada. Não apagamos nada: o material
          pode ter sido gasto de verdade — quem decide é quem estava na bancada. */}
      {servico.fichaForaDoPar && (
        <p className="flex items-start gap-1.5 rounded-md bg-amber-50 p-2 text-[11px] text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          A ficha aplicada ({servico.technicalSheetName}) não pertence mais a este serviço — a
          especificação mudou depois. O consumo foi mantido; aplique outra se for o caso.
        </p>
      )}

      {/* Depois da ficha, porque a máquina é consequência da receita: o equipamento vem dela e
          só então alguém confirma ou troca. */}
      <EquipamentoDoServico
        serviceId={serviceId}
        daFicha={(dados.equipamentosDaFicha ?? []) as any}
        usados={(dados.equipamentosUsados ?? []) as any}
        disponiveis={(dados.equipamentosDisponiveis ?? []) as any}
        podeEditar={podeEditar}
      />

      {/* ── Escolher a receita ─────────────────────────────────────────────────────────────── */}
      {podeEditar && (
        <div className="rounded-lg border border-zinc-200 p-2.5 dark:border-border">
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-zinc-700 dark:text-foreground/80">
            <FileText className="h-3.5 w-3.5" />
            {semNada ? "Qual ficha técnica você usou?" : "Aplicar outra ficha"}
          </p>
          {fichas.length === 0 ? (
            <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
              Nenhuma ficha cadastrada para {servico.servicoLabel}
              {servico.materialLabel ? ` × ${servico.materialLabel}` : ""}. Dá para registrar o
              consumo mesmo assim, material por material, em Catálogo → Serviços.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {fichas.map((f: any) => {
                const trocando = confirmandoTroca === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    disabled={aplicar.isPending}
                    onClick={() => {
                      if (semNada) aplicar.mutate({ serviceId, technicalSheetId: f.id });
                      else if (trocando) aplicar.mutate({ serviceId, technicalSheetId: f.id, substituir: true });
                      else setConfirmandoTroca(f.id);
                    }}
                    className={
                      trocando
                        ? "rounded-md border border-red-300 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700 dark:border-red-500/40 dark:bg-red-950/20 dark:text-red-300"
                        : "rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-700 hover:border-brand-300 hover:text-brand-700 disabled:opacity-50 dark:border-border dark:bg-card dark:text-muted-foreground"
                    }
                  >
                    {trocando ? `Substituir tudo por "${f.name}"?` : f.name}
                    {!trocando && (
                      <span className="ml-1 text-zinc-400">
                        {f._count.lines} {f._count.lines === 1 ? "item" : "itens"}
                      </span>
                    )}
                  </button>
                );
              })}
              {confirmandoTroca && (
                <button
                  type="button"
                  onClick={() => setConfirmandoTroca(null)}
                  className="rounded-md px-2 py-1 text-[11px] text-zinc-500 hover:text-zinc-800 dark:text-muted-foreground"
                >
                  Cancelar
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── O consumo ──────────────────────────────────────────────────────────────────────── */}
      <table className="w-full table-fixed text-xs">
        <thead className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 dark:border-border dark:text-muted-foreground">
          <tr>
            <th className="pb-1.5 text-left font-semibold">Insumo</th>
            <th className="w-[4.5rem] pb-1.5 text-right font-semibold">Qtd.</th>
            <th className="w-14 pb-1.5 text-right font-semibold">Fator</th>
            <th className="w-16 pb-1.5 text-right font-semibold">Bruta</th>
            {podeVerCusto && <th className="w-20 pb-1.5 text-right font-semibold">Custo</th>}
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {linhas.map((l: any) => (
            <LinhaDeConsumo
              key={l.id}
              linha={l}
              podeEditar={podeEditar}
              podeVerCusto={podeVerCusto}
              onQuantidade={(quantityNet) => atualizar.mutate({ consumptionId: l.id, quantityNet })}
              onFator={(correctionFactor) => atualizar.mutate({ consumptionId: l.id, correctionFactor })}
              onResolver={(materialLineId) => resolver.mutate({ consumptionId: l.id, materialLineId })}
              onRemover={() => remover.mutate({ consumptionId: l.id })}
            />
          ))}
          {semNada && (
            <tr>
              <td colSpan={podeVerCusto ? 6 : 5} className="py-4 text-center text-[11.5px] text-zinc-400 dark:text-muted-foreground">
                Nenhum material registrado neste serviço ainda.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {podeEditar && (
        <select
          value=""
          onChange={(e) => adicionarMaterial(e.target.value)}
          aria-label="Adicionar insumo ao serviço"
          className="h-8 w-full rounded-md border border-dashed border-zinc-300 bg-white px-2 text-xs text-zinc-600 dark:border-border dark:bg-card dark:text-muted-foreground"
        >
          <option value="">+ Adicionar insumo gasto</option>
          {grupos.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.itens.map((m: any) => (
                <option key={m.id} value={m.id}>
                  {rotuloDoMaterial(m)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      )}

      {podeVerCusto && linhas.length > 0 && (
        <div className="space-y-1 rounded-lg bg-zinc-50 p-3 dark:bg-muted/40">
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-600 dark:text-muted-foreground">Custo de insumos deste trabalho</span>
            <span className="font-semibold tabular-nums text-zinc-900 dark:text-foreground">
              {formatPriceCents(custo.totalCents)}
              {custo.emAberto && (
                <span className="ml-1 text-[10px] font-normal text-amber-600">+ o que for escolhido</span>
              )}
            </span>
          </div>
          <p className="text-[10.5px] leading-snug text-zinc-500 dark:text-muted-foreground">
            Custo de <strong>insumos</strong>: não inclui bancada, energia nem estrutura.
          </p>
        </div>
      )}

      {custo.semPreco.length > 0 && podeVerCusto && (
        <p className="flex items-start gap-1.5 rounded-md bg-amber-50 p-2 text-[11px] text-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Sem preço de compra: {custo.semPreco.join(", ")} — o custo está incompleto até cadastrar.
        </p>
      )}
    </div>
  );
}
